import React from "react";
import { Link } from "react-router-dom";
import { Award, ChevronLeft, ChevronRight, Eye, FileCheck2 } from "lucide-react";
import Headerdash from "./head";
import Sidebar from "./sidebar";

const resultTabs = [
  { label: "Speaking", href: "/mockpage/speakingresult" },
  { label: "Writing", href: "/mockpage/writingresult" },
  { label: "Reading", href: "/mockpage/readingresult" },
  { label: "Listening", href: "/mockpage/listeningresult" },
  { label: "Full Mock Test", href: "/mockpage/result" },
];

const scoreTone = (score) => {
  const value = Number(score);
  if (Number.isNaN(value)) return "bg-slate-100 text-gray-700 ring-slate-200";
  if (value >= 70) return "bg-green-50 text-green-700 ring-green-100";
  if (value >= 50) return "bg-blue-50 text-blue-700 ring-blue-100";
  if (value > 0) return "bg-amber-50 text-amber-700 ring-amber-100";
  return "bg-slate-100 text-gray-700 ring-slate-200";
};

const formatScore = (score) => {
  if (score === null || score === undefined || score === "") return "N/A";
  return score;
};

export default function MockResultPageLayout({
  activeSection,
  error,
  isLoading,
  items,
  pageNumbers,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  onPageChange,
}) {
  const visibleItems = items.slice(startIndex, endIndex);
  const title =
    activeSection === "Full Mock Test"
      ? "Full mock test results"
      : `${activeSection} mock results`;

  return (
    <div className="min-h-screen bg-slate-50 text-gray-700">
      <Headerdash />
      <Sidebar />

      <main className="min-h-screen pt-20 sm:pl-72">
        <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Mock Test
                </p>
                <h1 className="mt-1 text-2xl font-extrabold capitalize text-gray-900 sm:text-3xl">
                  {title}
                </h1>
                <p className="mt-2 text-sm text-gray-500">
                  Review completed attempts, average score, and open detailed scorecards.
                </p>
              </div>

              <div className="flex items-center gap-3 rounded-md bg-slate-50 p-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                  <FileCheck2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Attempts found
                  </p>
                  <p className="text-xl font-extrabold text-gray-900">
                    {items.length}
                  </p>
                </div>
              </div>
            </div>

            <nav aria-label="Mock result sections" className="mt-5 overflow-x-auto">
              <div className="flex min-w-max gap-2">
                {resultTabs.map((tab) => {
                  const isActive = tab.label === activeSection;

                  return (
                    <a
                      key={tab.href}
                      href={tab.href}
                      className={`inline-flex min-h-10 items-center rounded-md px-3 py-2 text-sm font-bold transition ${
                        isActive
                          ? "bg-purple-50 text-purple-800 ring-1 ring-purple-100"
                          : "border border-slate-200 bg-white text-gray-600 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-800"
                      }`}
                    >
                      {tab.label}
                    </a>
                  );
                })}
              </div>
            </nav>

            <div className="mt-6 overflow-hidden rounded-md border border-slate-200">
              <div className="grid grid-cols-[64px,minmax(180px,1fr),120px,120px] gap-4 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-500 max-md:hidden">
                <span>S. No</span>
                <span>Title</span>
                <span>Badge</span>
                <span className="text-right">Scorecard</span>
              </div>

              {isLoading && (
                <div className="space-y-3 p-4">
                  {[1, 2, 3, 4].map((item) => (
                    <div
                      key={item}
                      className="grid gap-4 rounded-md border border-slate-100 bg-white p-4 md:grid-cols-[64px,minmax(180px,1fr),120px,120px]"
                    >
                      <div className="h-5 rounded-sm bg-slate-100" />
                      <div className="h-5 rounded-sm bg-slate-100" />
                      <div className="h-5 rounded-sm bg-slate-100" />
                      <div className="h-5 rounded-sm bg-slate-100" />
                    </div>
                  ))}
                </div>
              )}

              {!isLoading && error && (
                <div className="p-6">
                  <div className="rounded-md border border-red-100 bg-red-50 p-4 text-sm font-semibold text-red-700">
                    {error}
                  </div>
                </div>
              )}

              {!isLoading && !error && visibleItems.length === 0 && (
                <div className="p-8 text-center">
                  <span className="mx-auto grid h-12 w-12 place-items-center rounded-md bg-purple-50 text-purple-700">
                    <FileCheck2 className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h2 className="mt-4 text-lg font-extrabold text-gray-900">
                    No mock results yet
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Completed {activeSection === "Full Mock Test" ? "full mock tests" : `${activeSection.toLowerCase()} mock tests`} will appear here.
                  </p>
                </div>
              )}

              {!isLoading &&
                !error &&
                visibleItems.map((item, index) => {
                  const absoluteIndex = startIndex + index + 1;
                  const displayScore = item.overallPrediction || item.averageScore;

                  return (
                    <article
                      key={item._id || absoluteIndex}
                      className="grid gap-4 border-t border-slate-100 bg-white px-4 py-4 transition first:border-t-0 hover:bg-slate-50 md:grid-cols-[64px,minmax(180px,1fr),120px,120px] md:items-center"
                    >
                      <div>
                        <p className="text-xs font-bold uppercase text-gray-400 md:hidden">
                          S. No
                        </p>
                        <p className="font-bold text-gray-800">{absoluteIndex}</p>
                      </div>

                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase text-gray-400 md:hidden">
                          Title
                        </p>
                        <h2 className="truncate text-sm font-bold text-gray-900 sm:text-base">
                          {item.mockTestName || "Untitled mock test"}
                        </h2>
                        <p className="mt-1 text-xs text-gray-500">
                          Predicted score: {formatScore(displayScore)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase text-gray-400 md:hidden">
                          Badge
                        </p>
                        <span
                          className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-sm font-bold ring-1 ${scoreTone(
                            displayScore
                          )}`}
                        >
                          <Award className="h-4 w-4" aria-hidden="true" />
                          {formatScore(displayScore)}
                        </span>
                      </div>

                      <div className="md:text-right">
                        <Link
                          className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md border border-violet-700 bg-violet-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm ring-1 ring-violet-200 transition hover:-translate-y-0.5 hover:border-violet-800 hover:bg-violet-700 hover:text-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                          style={{ backgroundColor: "#7c3aed", color: "#ffffff" }}
                          to={`/mockpage/result/${encodeURIComponent(item._id)}`}
                        >
                          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                          View Result
                        </Link>
                      </div>
                    </article>
                  );
                })}
            </div>

            {totalPages > 1 && (
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500">
                  Showing {startIndex + 1}-{endIndex} of {items.length}
                </p>
                <ol className="flex flex-wrap gap-1 text-sm font-bold">
                  <li>
                    <button
                      type="button"
                      onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-gray-700 transition hover:border-violet-200 hover:bg-violet-50 hover:text-violet-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-gray-700"
                    >
                      <span className="sr-only">Previous page</span>
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </li>

                  {pageNumbers.map((number) => (
                    <li key={number}>
                      <button
                        type="button"
                        onClick={() => onPageChange(number)}
                        className={`h-9 min-w-9 rounded-md px-3 transition ${
                          number === currentPage
                            ? "bg-violet-600 text-white shadow-sm"
                            : "border border-slate-200 bg-white text-gray-700 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-800"
                        }`}
                      >
                        {number}
                      </button>
                    </li>
                  ))}

                  <li>
                    <button
                      type="button"
                      onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-gray-700 transition hover:border-violet-200 hover:bg-violet-50 hover:text-violet-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-gray-700"
                    >
                      <span className="sr-only">Next page</span>
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </li>
                </ol>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
